import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatIDR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, MessageCircle } from "lucide-react";
import PondokIcon from "../components/PondokIcon";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";

const TODAY = () => {
  const d = new Date();
  return d.toLocaleDateString("id-ID", {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatDateShort = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

export default function ReportPrint() {
  const params = useParams();
  const { type } = params;

  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const isMonthly = type === "monthly";
  const isYearly = type === "yearly";
  const isRange = !!(params.start && params.end);
  const reportYear = Number(params.year) || new Date().getFullYear();
  const startDate = params.start || "";
  const endDate = params.end || "";

  useEffect(() => {
    let url, expUrl;
    if (isMonthly) { url = `/reports/monthly?year=${reportYear}`; expUrl = `/expenses/summary?year=${reportYear}`; }
    else if (isYearly) { url = `/reports/yearly`; expUrl = null; }  // yearly aggregates many years; skip per-year expense
    else if (isRange) { url = `/reports/range?start=${startDate}&end=${endDate}`; expUrl = `/expenses/summary?start=${startDate}&end=${endDate}`; }
    else { setLoading(false); return; }

    const calls = [api.get(url), api.get(`/settings`)];
    if (expUrl) calls.push(api.get(expUrl));

    Promise.all(calls)
      .then((results) => {
        setData(results[0].data);
        setSettings(results[1].data);
        if (results[2]) setExpenseSummary(results[2].data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isMonthly, isYearly, isRange, reportYear, startDate, endDate]);

  if (loading) return <div className="p-12 text-[#586D61]">Memuat laporan…</div>;
  if (!data) return <div className="p-12 text-[#D14D4D]">Data tidak ditemukan.</div>;

  // Normalize data shape
  let rows, rowLabelKey, rowsTitle, periodTitle, reportSubtitle;
  if (isYearly) {
    rows = data.years || [];
    rowLabelKey = "year";
    rowsTitle = "Rincian Per Tahun";
    periodTitle = "Rekap Tahunan";
    reportSubtitle = "Seluruh Tahun";
  } else if (isRange) {
    rows = (data.months || []).map((m) => ({ ...m, _label: m.month_name }));
    rowLabelKey = "_label";
    rowsTitle = "Rincian Per Bulan";
    periodTitle = "Rentang Kustom";
    reportSubtitle = `${formatDateShort(data.start)} — ${formatDateShort(data.end)}`;
  } else {
    rows = (data.months || []).map((m) => ({ ...m, _label: m.month_name }));
    rowLabelKey = "_label";
    rowsTitle = "Rincian Per Bulan";
    periodTitle = `Bulanan ${reportYear}`;
    reportSubtitle = `Tahun ${reportYear}`;
  }

  // Totals
  const totalRevenue = isYearly
    ? rows.reduce((s, r) => s + (r.revenue || 0), 0)
    : (data.total_revenue || 0);
  const totalBookings = isYearly
    ? rows.reduce((s, r) => s + (r.bookings || 0), 0)
    : (data.total_bookings || 0);
  const totalGuests = isYearly
    ? rows.reduce((s, r) => s + (r.guests || 0), 0)
    : (data.total_guests || 0);
  const totalDeposits = isYearly
    ? rows.reduce((s, r) => s + (r.deposits || 0), 0)
    : (data.total_deposits || rows.reduce((s, r) => s + (r.deposits || 0), 0));
  const totalOutstanding = isYearly
    ? rows.reduce((s, r) => s + (r.outstanding || 0), 0)
    : (data.total_outstanding || totalRevenue - totalDeposits);

  const totalExpenses = expenseSummary?.total || 0;
  const totalProfit = totalRevenue - totalExpenses;
  const showFinancials = !isYearly;  // expenses only meaningful per-year/range

  // Build WhatsApp summary message
  const buildWaMessage = () => {
    const homestay = settings?.homestay_name || "Meos Ambower Homestay";
    const lines = [];
    lines.push(`*${homestay}*`);
    lines.push(`_Laporan ${periodTitle}_`);
    lines.push(`${reportSubtitle}`);
    lines.push("");
    lines.push("*━━━ RINGKASAN ━━━*");
    lines.push(`• Pendapatan: *${formatIDR(totalRevenue)}*`);
    if (showFinancials && totalExpenses > 0) {
      lines.push(`• Pengeluaran: ${formatIDR(totalExpenses)}`);
      lines.push(`• *Profit Bersih: ${formatIDR(totalProfit)}*`);
    }
    lines.push(`• Total Booking: *${totalBookings}*`);
    lines.push(`• Total Tamu: *${totalGuests}*`);
    lines.push(`• DP Terbayar: ${formatIDR(totalDeposits)}`);
    lines.push(`• Sisa Tagihan: ${formatIDR(totalOutstanding)}`);
    if (rows.length > 0) {
      lines.push("");
      lines.push(`*━━━ RINCIAN ${isYearly ? "TAHUNAN" : "BULANAN"} ━━━*`);
      const limit = 12;
      const shown = rows.slice(0, limit);
      for (const r of shown) {
        const label = isYearly ? r.year : r.month_name;
        lines.push(`• ${label}: ${formatIDR(r.revenue || 0)} (${r.bookings} booking, ${r.guests} tamu)`);
      }
      if (rows.length > limit) lines.push(`… dan ${rows.length - limit} baris lainnya`);
    }
    lines.push("");
    lines.push(`_Dibuat otomatis ${TODAY()}_`);
    return lines.join("\n");
  };

  const sendToWhatsApp = () => {
    const msg = encodeURIComponent(buildWaMessage());
    const url = `https://wa.me/?text=${msg}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Chart data — use short labels + revenue
  const chartData = rows.map((r) => ({
    name: isYearly ? String(r.year) : (r.month_name || r._label || ""),
    revenue: r.revenue || 0,
  }));
  const hasData = chartData.some((c) => c.revenue > 0);

  return (
    <div className="min-h-screen bg-[#FDFBF7] print:bg-white" data-testid="report-print-page">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden sticky top-0 z-10 border-b border-[#EBE6DE] bg-white/80 backdrop-blur px-8 py-4 flex items-center justify-between">
        <Link to="/reports" className="flex items-center gap-2 text-[#586D61] hover:text-[#172E21] text-sm">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Laporan
        </Link>
        <div className="flex items-center gap-2">
          <Button
            data-testid="btn-send-whatsapp"
            onClick={sendToWhatsApp}
            variant="outline"
            className="border-[#1B5E3F]/40 text-[#1B5E3F] hover:bg-[#1B5E3F]/8 hover:text-[#1B5E3F] rounded-xl gap-2"
          >
            <MessageCircle className="h-4 w-4" /> Kirim via WhatsApp
          </Button>
          <Button
            data-testid="btn-print-report"
            onClick={() => window.print()}
            className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
          >
            <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* Print paper */}
      <div className="max-w-4xl mx-auto my-8 bg-white border border-[#EBE6DE] rounded-2xl shadow-sm p-10 print:border-0 print:shadow-none print:rounded-none print:my-0 print:max-w-full print:p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 border-b border-[#EBE6DE] pb-6">
          <div className="flex items-start gap-4">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-14 w-14 rounded-2xl object-contain bg-white border border-[#EBE6DE] print:rounded-xl" data-testid="report-print-logo" />
            ) : (
              <div className="h-14 w-14 rounded-2xl bg-[#D16B4D] flex items-center justify-center print:rounded-xl">
                <PondokIcon className="h-7 w-7 text-white" strokeWidth={1.8} />
              </div>
            )}
            <div>
              <div className="font-heading text-xl font-semibold text-[#172E21]">
                {settings?.homestay_name || "Meos Ambower Homestay"}
              </div>
              <div className="text-xs text-[#586D61] mt-0.5">{settings?.homestay_address || ""}</div>
              <div className="text-xs text-[#586D61]">
                {settings?.homestay_phone}
                {settings?.homestay_email ? ` • ${settings.homestay_email}` : ""}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#D16B4D]">
              Laporan Keuangan
            </div>
            <div className="font-heading text-2xl font-medium text-[#172E21] mt-1">
              {periodTitle}
            </div>
            <div className="text-xs text-[#586D61] mt-1">
              {reportSubtitle} • Dicetak {TODAY()}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <SummaryBlock label="Total Pendapatan" value={formatIDR(totalRevenue)} accent="#D16B4D" />
          <SummaryBlock label="Total Booking" value={String(totalBookings)} accent="#2C7A51" />
          <SummaryBlock label="Total Tamu" value={String(totalGuests)} accent="#082618" />
        </div>
        {showFinancials && totalExpenses > 0 && (
          <div className="grid grid-cols-3 gap-4 mt-3" data-testid="print-financial-summary">
            <SummaryBlock label="Pengeluaran" value={`-${formatIDR(totalExpenses)}`} accent="#D14D4D" small />
            <SummaryBlock label="Profit Bersih" value={formatIDR(totalProfit)} accent={totalProfit >= 0 ? "#2C7A51" : "#D14D4D"} small />
            <SummaryBlock label="Margin" value={totalRevenue > 0 ? `${((totalProfit / totalRevenue) * 100).toFixed(1)}%` : "—"} accent="#082618" small />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 mt-3">
          <SummaryBlock label="DP Terbayar" value={formatIDR(totalDeposits)} accent="#2C7A51" small />
          <SummaryBlock label="Sisa Tagihan" value={formatIDR(totalOutstanding)} accent="#D99338" small />
        </div>

        {/* Chart */}
        {hasData && (
          <div className="mt-8" data-testid="print-chart">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61] mb-3">
              Grafik Pendapatan
            </div>
            <div className="h-64 bg-[#FDFBF7]/60 rounded-xl p-3 border border-[#EBE6DE]/70 print:border-[#EBE6DE]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: "#586D61" }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={chartData.length > 8 ? -35 : 0}
                    textAnchor={chartData.length > 8 ? "end" : "middle"}
                    height={chartData.length > 8 ? 55 : 30}
                  />
                  <YAxis
                    tickFormatter={(v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v)}
                    tick={{ fontSize: 10, fill: "#586D61" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Bar dataKey="revenue" fill="#D16B4D" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                    <LabelList
                      dataKey="revenue"
                      position="top"
                      formatter={(v) => (v > 0 ? (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}jt` : `${(v / 1000).toFixed(0)}rb`) : "")}
                      style={{ fontSize: 10, fill: "#172E21", fontWeight: 600 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="mt-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61] mb-3">
            {rowsTitle}
          </div>
          <table className="w-full text-sm" data-testid="report-print-table">
            <thead>
              <tr className="bg-[#FDFBF7] border-b-2 border-[#EBE6DE]">
                <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">
                  {isYearly ? "Tahun" : "Bulan"}
                </th>
                <th className="text-center py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Booking</th>
                <th className="text-center py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Tamu</th>
                <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Pendapatan</th>
                <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">DP</th>
                <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Sisa</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#586D61]">
                    Belum ada data untuk periode ini.
                  </td>
                </tr>
              )}
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-[#EBE6DE]/60">
                  <td className="py-2.5 px-3 font-medium text-[#172E21]">{row[rowLabelKey]}</td>
                  <td className="text-center py-2.5 px-3 font-mono text-[#172E21]">{row.bookings}</td>
                  <td className="text-center py-2.5 px-3 font-mono text-[#172E21]">{row.guests}</td>
                  <td className="text-right py-2.5 px-3 font-mono text-[#172E21] font-medium">{formatIDR(row.revenue)}</td>
                  <td className="text-right py-2.5 px-3 font-mono text-[#2C7A51]">{formatIDR(row.deposits)}</td>
                  <td className="text-right py-2.5 px-3 font-mono text-[#D99338]">{formatIDR(row.outstanding)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#FDFBF7] border-t-2 border-[#082618]">
                <td className="py-3 px-3 font-bold text-[#172E21] uppercase text-xs tracking-wider">Total {reportSubtitle}</td>
                <td className="text-center py-3 px-3 font-mono font-bold text-[#172E21]">{totalBookings}</td>
                <td className="text-center py-3 px-3 font-mono font-bold text-[#172E21]">{totalGuests}</td>
                <td className="text-right py-3 px-3 font-mono font-bold text-[#D16B4D]">{formatIDR(totalRevenue)}</td>
                <td className="text-right py-3 px-3 font-mono font-bold text-[#2C7A51]">{formatIDR(totalDeposits)}</td>
                <td className="text-right py-3 px-3 font-mono font-bold text-[#D99338]">{formatIDR(totalOutstanding)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-[#EBE6DE] flex items-center justify-between text-xs text-[#586D61]">
          <div>
            Laporan ini dihasilkan otomatis oleh sistem PondokRaja.
            {settings?.bank_info && <div className="mt-1">{settings.bank_info}</div>}
          </div>
          <div className="text-right">
            <div className="font-heading font-medium text-[#172E21]">PondokRaja</div>
            <div>Booking Manager</div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function SummaryBlock({ label, value, accent, small }) {
  return (
    <div
      className="rounded-xl border p-4 print:rounded-lg"
      style={{ borderColor: `${accent}40`, backgroundColor: `${accent}08` }}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#586D61]">{label}</div>
      <div className={`font-mono font-medium tracking-tight mt-1 ${small ? "text-lg" : "text-2xl"}`} style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}
